import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetCompaniesQuery } from '../../store/api/company';
import type { CompanyFilters } from '../../store/api/company';
import type { BFTableColumn, SortConfig, FilterConfig } from '../../components/BFTable/BFTable';
import type { CompanyDto } from '../../types/company';
import { CompanySize } from '../../constants/enums';

const PAGE_SIZE_DEFAULT = 40;
const PAGE_SIZE_OPTIONS = [20, 40, 100];

export interface CompanyRow extends Record<string, unknown> {
    id: number;
    uid: string;
    name: string;
    size: string;
    ic: string;
    vat: string;
    street: string;
    city: string;
    zip: string;
    businessSubjects: string;
    representative: string;
    priceList: string;
    contactLast: string;
    contactNext: string;
    state: string;
    status: string;
}

function mapToRow(c: CompanyDto): CompanyRow {
    return {
        id: c.id,
        uid: c.registrationId ?? `${c.id}`,
        name: c.name,
        size: c.size ?? '—',
        ic: c.ic ?? '—',
        vat: c.vat ?? '—',
        street: c.street ?? '—',
        city: c.municipality || c.village || c.cityDistrict || '—',
        zip: c.zipCode ?? '—',
        businessSubjects: c.mainScope ?? '—',
        representative: '—',
        priceList: '—',
        contactLast: '—',
        contactNext: '—',
        state: c.country ?? '—',
        status: c.status ?? '—',
    };
}

// Maps BFTable column keys to CompanyFilters fields for BE queries
const COLUMN_TO_FILTER: Partial<Record<string, keyof CompanyFilters>> = {
    name: 'name',
    ic: 'ic',
    vat: 'vat',
    street: 'street',
    city: 'municipality',
    zip: 'zipCode',
};

export default function useCompanies() {
    const { t } = useTranslation();

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
    const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
    const [filters, setFilters] = useState<CompanyFilters>({});
    const [searchValue, setSearchValue] = useState('');
    const [selectedSizes, setSelectedSizes] = useState<CompanySize[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<(string | number)[]>([]);
    const [tableFilterConfig, setTableFilterConfig] = useState<FilterConfig>({});
    const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);

    const queryArg = {
        page,
        pageSize,
        orderBy: sortConfig?.key,
        asc: sortConfig ? sortConfig.direction === 'asc' : undefined,
        filters: {
            ...filters,
            ...(searchValue ? { ic: searchValue, vat: searchValue } : {}),
            ...(selectedSizes.length > 0 ? { size: selectedSizes } : {}),
            ...Object.entries(tableFilterConfig).reduce<Partial<CompanyFilters>>((acc, [key, val]) => {
                const field = COLUMN_TO_FILTER[key];
                if (field && val?.value) acc[field] = [val.value] as any;
                return acc;
            }, {}),
        },
    };

    const isLoadMoreRef = useRef(false);
    const [loadState, setLoadState] = useState<{ rows: CompanyRow[]; total: number; totalPages: number }>({
        rows: [],
        total: 0,
        totalPages: 1,
    });
    const [isLoadMoreActive, setIsLoadMoreActive] = useState(false);

    const { data, isFetching } = useGetCompaniesQuery(queryArg);

    useEffect(() => {
        if (!data) return;
        const newRows = data.items.map(mapToRow);
        if (isLoadMoreRef.current) {
            setLoadState((prev) => ({
                rows: [...prev.rows, ...newRows],
                total: data.totalCount,
                totalPages: data.totalPages,
            }));
        } else {
            setLoadState({ rows: newRows, total: data.totalCount, totalPages: data.totalPages });
        }
        isLoadMoreRef.current = false;
        setIsLoadMoreActive(false);
    }, [data]);

    const { rows } = loadState;
    const { total } = loadState;
    const { totalPages } = loadState;

    const tableFilterCount = Object.values(tableFilterConfig).filter((f) => f?.value).length;
    const activeFilterCount =
        [searchValue ? 1 : 0, selectedSizes.length > 0 ? 1 : 0].reduce((a, b) => a + b, 0) + tableFilterCount;

    const handleReset = useCallback(() => {
        setSearchValue('');
        setSelectedSizes([]);
        setFilters({});
        setTableFilterConfig({});
        setPage(1);
    }, []);

    const handleTableFilter = useCallback((config: FilterConfig) => {
        setTableFilterConfig(config);
        setPage(1);
    }, []);

    const handleSort = useCallback((config: SortConfig | null) => {
        setSortConfig(config);
        setPage(1);
    }, []);

    const handlePageChange = useCallback((newPage: number, newPageSize: number) => {
        setPage(newPage);
        setPageSize(newPageSize);
    }, []);

    const handleLoadMore = useCallback(() => {
        isLoadMoreRef.current = true;
        setIsLoadMoreActive(true);
        setPage((prev) => prev + 1);
    }, []);

    const handleRowClick = useCallback((record: CompanyRow) => {
        setSelectedCompanyId(record.id);
    }, []);

    const sizeOptions = Object.values(CompanySize).map((s) => ({
        value: s,
        label: t(`enums.companySize.${s}` as const),
    }));

    const columns: BFTableColumn<CompanyRow>[] = [
        { key: 'state', dataIndex: 'state', title: t('selectedCompany.generic.state'), width: 50, sortable: false },
        { key: 'status', dataIndex: 'status', title: t('tables.companyTable.status'), width: 70, sortable: true },
        { key: 'uid', dataIndex: 'uid', title: t('tables.companyTable.registrationId'), width: 120, sortable: true },
        {
            key: 'name',
            dataIndex: 'name',
            title: t('tables.companyTable.name'),
            width: 200,
            sortable: true,
            filterable: true,
        },
        { key: 'size', dataIndex: 'size', title: t('tables.companyTable.size'), width: 60, sortable: true },
        { key: 'ic', dataIndex: 'ic', title: t('tables.companyTable.ic'), width: 90, sortable: true },
        { key: 'vat', dataIndex: 'vat', title: t('tables.companyTable.vat'), width: 110, sortable: true },
        { key: 'street', dataIndex: 'street', title: t('tables.companyTable.street'), width: 160, sortable: true },
        { key: 'city', dataIndex: 'city', title: t('tables.companyTable.municipality'), width: 130, sortable: true },
        { key: 'zip', dataIndex: 'zip', title: t('tables.companyTable.zipCode'), width: 70, sortable: true },
        {
            key: 'businessSubjects',
            dataIndex: 'businessSubjects',
            title: t('tables.companyTable.mainScopeId'),
            width: 200,
            sortable: true,
        },
        { key: 'representative', dataIndex: 'representative', title: 'Representative', width: 130 },
        { key: 'priceList', dataIndex: 'priceList', title: 'Price List', width: 130 },
        { key: 'contactLast', dataIndex: 'contactLast', title: t('selectedCompany.generic.lastContact'), width: 120 },
        { key: 'contactNext', dataIndex: 'contactNext', title: t('selectedCompany.generic.nextContact'), width: 120 },
    ];

    return {
        rows,
        columns,
        total,
        totalPages,
        page,
        pageSize,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
        isFetching: isFetching && !isLoadMoreActive,
        isLoadMoreFetching: isFetching && isLoadMoreActive,
        sortConfig,
        tableFilterConfig,
        activeFilterCount,
        searchValue,
        selectedSizes,
        sizeOptions,
        setSearchValue,
        setSelectedSizes,
        handleReset,
        handleSort,
        handlePageChange,
        handleLoadMore,
        handleTableFilter,
        selectedRowKeys,
        setSelectedRowKeys,
        selectedCompanyId,
        setSelectedCompanyId,
        handleRowClick,
        t,
    };
}
